import { get, post, put } from './coreApi';

export type EzeeProvider = 'openai' | 'digitalocean';
export type EzeeMode = 'test' | 'live';

export interface EzeeAiProviderSettings {
  provider: EzeeProvider | string;
  mode: EzeeMode;
  testApiKey: string;
  liveApiKey: string;
  testModel: string;
  liveModel: string;
  baseUrl: string;
  hasTestApiKey: boolean;
  hasLiveApiKey: boolean;
  testApiKeyMasked: string;
  liveApiKeyMasked: string;
  useEnvFallback: boolean;
}

export interface EzeeAiModel {
  id: string;
  ownedBy?: string | null;
}

interface ProviderSettingsResponse {
  success: boolean;
  settings: EzeeAiProviderSettings;
}

interface ProviderModelsResponse {
  success: boolean;
  provider: string;
  mode: string;
  source: string;
  models: EzeeAiModel[];
}

interface ProviderTestResponse extends ProviderModelsResponse {
  message: string;
  baseUrl?: string;
  model?: string | null;
  configuredModelExists?: boolean;
  availableModelCount?: number;
}

export const getEzeeAiSettings = async (
  instanceUniqueId: string
): Promise<EzeeAiProviderSettings> => {
  const response = await get<ProviderSettingsResponse>(
    `/ai/instances/${encodeURIComponent(instanceUniqueId)}/provider-settings`
  );
  return response.settings;
};

export const saveEzeeAiSettings = async (
  instanceUniqueId: string,
  settings: Pick<
    EzeeAiProviderSettings,
    | 'provider'
    | 'mode'
    | 'testApiKey'
    | 'liveApiKey'
    | 'testModel'
    | 'liveModel'
    | 'baseUrl'
    | 'useEnvFallback'
  >
): Promise<EzeeAiProviderSettings> => {
  const response = await put<ProviderSettingsResponse>(
    `/ai/instances/${encodeURIComponent(instanceUniqueId)}/provider-settings`,
    settings
  );
  return response.settings;
};

export const listEzeeAiModels = async (
  instanceUniqueId: string
): Promise<ProviderModelsResponse> =>
  get<ProviderModelsResponse>(
    `/ai/instances/${encodeURIComponent(instanceUniqueId)}/provider-settings/models`
  );

export const testEzeeAiConnection = async (
  instanceUniqueId: string
): Promise<ProviderTestResponse> =>
  post<ProviderTestResponse>(
    `/ai/instances/${encodeURIComponent(instanceUniqueId)}/provider-settings/test`,
    {}
  );


export interface EzeeNervousSystemState {
  enabled: boolean;
  emergencyStop: boolean;
  reason?: string | null;
  stoppedAt?: string | null;
  stoppedByUserId?: number | null;
  resumedAt?: string | null;
  resumedByUserId?: number | null;
}

export interface EzeeDomainEventQueueStatus {
  pending: number;
  processing: number;
  retry: number;
  failed: number;
  latestEvent?: {
    id: number;
    eventType: string;
    status: string;
    processedAt?: string | null;
    createdAt?: string | null;
    updatedAt?: string | null;
  } | null;
}

interface NervousSystemResponse {
  success: boolean;
  instanceUniqueId: string;
  state: EzeeNervousSystemState;
  active: boolean;
  deployment?: { runtimeArmed: boolean; backgroundWorkersArmed: boolean };
  eventQueue?: EzeeDomainEventQueueStatus;
}

export const getEzeeNervousSystemState = async (
  instanceUniqueId: string
): Promise<NervousSystemResponse> =>
  get<NervousSystemResponse>(
    `/ai/instances/${encodeURIComponent(instanceUniqueId)}/nervous-system`
  );

export const updateEzeeNervousSystemState = async (
  instanceUniqueId: string,
  payload: Partial<Pick<EzeeNervousSystemState, 'enabled' | 'emergencyStop' | 'reason'>>
): Promise<NervousSystemResponse> =>
  put<NervousSystemResponse>(
    `/ai/instances/${encodeURIComponent(instanceUniqueId)}/nervous-system`,
    payload
  );


export interface EzeeKnowledgeStatus {
  documents: number;
  enabledDocuments: number;
  chunks: number;
  latestIndexedAt?: string | null;
}

interface EzeeKnowledgeStatusResponse {
  success: boolean;
  status: EzeeKnowledgeStatus;
}

interface EzeeKnowledgeSyncResponse extends EzeeKnowledgeStatusResponse {
  sync: {
    documents: number;
    indexed: number;
    unchanged: number;
    chunks: number;
  };
}

export const getEzeeKnowledgeStatus = async (
  instanceUniqueId: string
): Promise<EzeeKnowledgeStatus> => {
  const response = await get<EzeeKnowledgeStatusResponse>(
    `/ai/instances/${encodeURIComponent(instanceUniqueId)}/knowledge/status`
  );
  return response.status;
};

export const syncEzeeKnowledge = async (
  instanceUniqueId: string
): Promise<EzeeKnowledgeSyncResponse> =>
  post<EzeeKnowledgeSyncResponse>(
    `/ai/instances/${encodeURIComponent(instanceUniqueId)}/knowledge/sync`,
    {}
  );


export type EzeeImprovementMode =
  | 'observe'
  | 'recommend'
  | 'simulate'
  | 'prepare'
  | 'auto_safe'
  | 'engineering_proposal';

export interface EzeeImprovementAutonomySettings {
  mode: EzeeImprovementMode;
  minRecommendationOccurrences: number;
  minAutoSafeConfidence: number;
  allowedSafeActions: string[];
  reflectionEnabled: boolean;
  evaluationsEnabled: boolean;
}

export interface EzeeImprovementDashboard {
  autonomy: EzeeImprovementAutonomySettings;
  openObservations: number;
  recommendationCount: number;
  recommendationsByStatus: Record<string, number>;
  readyForReview: number;
  verified: number;
  regressed: number;
  acceptanceRate?: number | null;
  verificationSuccessRate?: number | null;
  healthScore?: number | null;
  evalCases: number;
  latestEvaluation?: {
    id: number;
    score: number;
    passedCases: number;
    failedCases: number;
    totalCases: number;
    finishedAt?: string | null;
  } | null;
}

export interface EzeeImprovementReview {
  id: number;
  recommendationId: number;
  reviewerUserId?: number | null;
  decision: string;
  reason?: string | null;
  requestedChanges?: any;
  createdAt: string;
}

export interface EzeeImprovementRecommendation {
  id: number;
  instanceUniqueId: string;
  category: string;
  fingerprint: string;
  title: string;
  problem: string;
  evidenceSummary?: any;
  rootCauseHypothesis?: string | null;
  affectedArea?: any[];
  proposedChange?: any;
  confidence: number | string;
  impact: string;
  risk: string;
  verificationPlan?: any;
  status: string;
  occurrenceCount: number;
  sourceObservationIds?: number[];
  safeAction?: any;
  reviewSummary?: any;
  simulationSummary?: any;
  verificationSummary?: any;
  firstObservedAt?: string | null;
  lastObservedAt?: string | null;
  implementedAt?: string | null;
  verifiedAt?: string | null;
  reviews?: EzeeImprovementReview[];
  createdAt: string;
  updatedAt: string;
}

export interface EzeeImprovementObservation {
  id: number;
  instanceUniqueId: string;
  category: string;
  severity: string;
  fingerprint: string;
  summary: string;
  evidence?: any;
  sourceType?: string | null;
  sourceId?: string | null;
  userId?: number | null;
  conversationId?: number | null;
  entityType?: string | null;
  entityId?: string | null;
  status: string;
  occurredAt: string;
}

export interface EzeeEvaluationCase {
  id: number;
  caseKey: string;
  category: string;
  name: string;
  inputPrompt: string;
  expectedBehavior?: any;
  status: string;
  recommendationId?: number | null;
  createdAt: string;
}

export interface EzeeEvaluationRun {
  id: number;
  scope: string;
  status: string;
  totalCases: number;
  passedCases: number;
  failedCases: number;
  score?: number | string | null;
  model?: string | null;
  provider?: string | null;
  finishedAt?: string | null;
  createdAt: string;
}

export const getEzeeImprovementDashboard = async (
  instanceUniqueId: string
): Promise<EzeeImprovementDashboard> => {
  const response = await get<{ success: boolean; dashboard: EzeeImprovementDashboard }>(
    `/ai/instances/${encodeURIComponent(instanceUniqueId)}/improvements/dashboard`
  );
  return response.dashboard;
};

export const getEzeeImprovementAutonomy = async (
  instanceUniqueId: string
): Promise<EzeeImprovementAutonomySettings> => {
  const response = await get<{ success: boolean; settings: EzeeImprovementAutonomySettings }>(
    `/ai/instances/${encodeURIComponent(instanceUniqueId)}/improvements/autonomy`
  );
  return response.settings;
};

export const updateEzeeImprovementAutonomy = async (
  instanceUniqueId: string,
  payload: Partial<EzeeImprovementAutonomySettings>
): Promise<EzeeImprovementAutonomySettings> => {
  const response = await put<{ success: boolean; settings: EzeeImprovementAutonomySettings }>(
    `/ai/instances/${encodeURIComponent(instanceUniqueId)}/improvements/autonomy`,
    payload
  );
  return response.settings;
};

export const listEzeeImprovementRecommendations = async (
  instanceUniqueId: string
): Promise<EzeeImprovementRecommendation[]> => {
  const response = await get<{ success: boolean; recommendations: EzeeImprovementRecommendation[] }>(
    `/ai/instances/${encodeURIComponent(instanceUniqueId)}/improvements/recommendations?limit=100`
  );
  return response.recommendations || [];
};

export const listEzeeImprovementObservations = async (
  instanceUniqueId: string,
  fingerprint?: string
): Promise<EzeeImprovementObservation[]> => {
  const query = fingerprint ? `?fingerprint=${encodeURIComponent(fingerprint)}&limit=200` : '?limit=100';
  const response = await get<{ success: boolean; observations: EzeeImprovementObservation[] }>(
    `/ai/instances/${encodeURIComponent(instanceUniqueId)}/improvements/observations${query}`
  );
  return response.observations || [];
};

export const runEzeeImprovementCycle = async (
  instanceUniqueId: string
): Promise<any> =>
  post<any>(
    `/ai/instances/${encodeURIComponent(instanceUniqueId)}/improvements/run`,
    {}
  );

export const reviewEzeeImprovementRecommendation = async (
  instanceUniqueId: string,
  recommendationId: number,
  payload: {
    decision: 'approved' | 'rejected' | 'needs_changes' | 'implemented' | 'archived';
    reason?: string;
    requestedChanges?: any;
  }
): Promise<EzeeImprovementRecommendation> => {
  const response = await post<{ success: boolean; recommendation: EzeeImprovementRecommendation }>(
    `/ai/instances/${encodeURIComponent(instanceUniqueId)}/improvements/recommendations/${recommendationId}/review`,
    payload
  );
  return response.recommendation;
};

export const prepareEzeeImprovementRecommendation = async (
  instanceUniqueId: string,
  recommendationId: number
): Promise<EzeeImprovementRecommendation> => {
  const response = await post<{ success: boolean; recommendation: EzeeImprovementRecommendation }>(
    `/ai/instances/${encodeURIComponent(instanceUniqueId)}/improvements/recommendations/${recommendationId}/prepare`,
    {}
  );
  return response.recommendation;
};

export const simulateEzeeImprovementRecommendation = async (
  instanceUniqueId: string,
  recommendationId: number
): Promise<any> => {
  const response = await post<{ success: boolean; simulation: any }>(
    `/ai/instances/${encodeURIComponent(instanceUniqueId)}/improvements/recommendations/${recommendationId}/simulate`,
    {}
  );
  return response.simulation;
};

export const verifyEzeeImprovementRecommendation = async (
  instanceUniqueId: string,
  recommendationId: number
): Promise<any> => {
  const response = await post<{ success: boolean; verification: any }>(
    `/ai/instances/${encodeURIComponent(instanceUniqueId)}/improvements/recommendations/${recommendationId}/verify`,
    {}
  );
  return response.verification;
};

export const listEzeeEvaluationCases = async (
  instanceUniqueId: string
): Promise<EzeeEvaluationCase[]> => {
  const response = await get<{ success: boolean; cases: EzeeEvaluationCase[] }>(
    `/ai/instances/${encodeURIComponent(instanceUniqueId)}/improvements/evaluations/cases?limit=100`
  );
  return response.cases || [];
};

export const listEzeeEvaluationRuns = async (
  instanceUniqueId: string
): Promise<EzeeEvaluationRun[]> => {
  const response = await get<{ success: boolean; runs: EzeeEvaluationRun[] }>(
    `/ai/instances/${encodeURIComponent(instanceUniqueId)}/improvements/evaluations/runs?limit=30`
  );
  return response.runs || [];
};

export const runEzeeEvaluationSuite = async (
  instanceUniqueId: string,
  recommendationId?: number
): Promise<EzeeEvaluationRun> => {
  const response = await post<{ success: boolean; run: EzeeEvaluationRun }>(
    `/ai/instances/${encodeURIComponent(instanceUniqueId)}/improvements/evaluations/run`,
    recommendationId ? { recommendationId } : {}
  );
  return response.run;
};
